import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, MessageCircle, ShieldCheck, Sparkles, Users } from "lucide-react";

export default function CommunityLanding() {
  const { user } = useAuth();
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    api.get("/community/channels")
      .then((r) => setChannels((r.data || []).slice(0, 3)))
      .catch(() => setChannels([]));
  }, []);

  const canAccessCommunity = user?.role === "admin" || user?.has_active_formation === true;
  const ctaTarget = !user ? "/login" : canAccessCommunity ? "/community/app" : "/catalog";
  const ctaLabel = !user ? "Se connecter pour accéder" : canAccessCommunity ? "Accéder à la communauté" : "Découvrir les formations";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="community-landing-page">
      <section className="card p-8 md:p-12 overflow-hidden relative bg-gradient-to-br from-emerald-50 to-amber-50">
        <div className="absolute right-[-120px] top-[-120px] h-72 w-72 rounded-full bg-brand-orange/20" />
        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="label mb-3">Communauté SF Learning</p>
            <h1 className="font-heading text-4xl md:text-5xl font-black leading-tight">
              Avance entouré, soutenu et challengé.
            </h1>
            <p className="mt-5 text-gray-700 text-lg max-w-xl">
              L’espace communautaire est réservé aux membres SF Learning. Tu peux y poser tes questions, partager tes victoires, demander de l’aide sur une leçon et progresser avec des personnes engagées dans la même transformation.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={ctaTarget} className="btn-primary" data-testid="community-main-cta">
                {ctaLabel} <ArrowRight className="w-4 h-4" />
              </Link>

              {!user && (
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-3 font-bold text-white shadow-md transition-all duration-200 hover:scale-105 hover:shadow-xl"
                  data-testid="community-register-cta"
                >
                  Rejoindre SF Learning
                </Link>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { icon: MessageCircle, title: "Discussions", text: "Pose tes questions et échange autour des modules." },
              { icon: Users, title: "Membres", text: "Retrouve les apprenants qui avancent avec toi." },
              { icon: Sparkles, title: "Progression", text: "Partage tes déclics, tes victoires et tes blocages." },
              { icon: ShieldCheck, title: "Cadre bienveillant", text: "Signalement possible si une discussion dérape." },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-white/80 rounded-2xl p-5 shadow-sm border border-white">
                <Icon className="w-7 h-7 text-brand-green mb-3" />
                <h3 className="font-heading font-bold text-lg">{title}</h3>
                <p className="text-sm text-gray-600 mt-1">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <p className="label mb-2">Aperçu</p>
            <h2 className="font-heading text-3xl font-black">Les espaces communautaires</h2>
          </div>

          {canAccessCommunity && (
            <Link to="/community/app" className="btn-ghost">
              Ouvrir l’espace privé <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {channels.length === 0 ? (
            <div className="card p-6 md:col-span-3 text-gray-500">
              Les espaces communautaires seront bientôt disponibles.
            </div>
          ) : channels.map((channel) => {
            const banner = channel.banner_image ? fileUrl(channel.banner_image) : "";
            return (
              <div key={channel.channel_id} className="card overflow-hidden">
                <div className="h-32 bg-gradient-to-br from-emerald-100 to-amber-100">
                  {banner && <img src={banner} alt={channel.name} className="h-full w-full object-cover" />}
                </div>

                <div className="p-5">
                  <h3 className="font-heading text-xl font-bold">{channel.name}</h3>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-3">
                    {channel.description || "Un espace pour échanger, progresser et rester entouré."}
                  </p>
                  <p className="text-xs text-gray-500 mt-4">
                    {channel.member_count || 0} membre{(channel.member_count || 0) > 1 ? "s" : ""}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}